import { z } from 'zod';

/**
 * Every string enum in the domain, as a Zod schema + its inferred TS union.
 * House convention: the schema value and the type share one name
 * (`export const Tier = z.enum([...]); export type Tier = z.infer<typeof Tier>;`)
 * so `Tier.parse(x)` and `let t: Tier` read the same. Values are the EXACT
 * strings persisted in Postgres — enums are `text` + Zod, never pg enums
 * (see docs/idea/data-model.md). Add a value here, nowhere else.
 */

// --- Identity & accounts ---------------------------------------------------

/** `accounts.kind` — one user = one personal account (multi-tenant seam kept). */
export const AccountKind = z.enum(['personal']);
export type AccountKind = z.infer<typeof AccountKind>;

/** `accounts.tier` — maps to limits + model policy in one place (tiers.ts). */
export const Tier = z.enum(['free', 'starter', 'plus', 'pro', 'max']);
export type Tier = z.infer<typeof Tier>;

/** `users.theme` — `auto` follows `prefers-color-scheme`; override wins. */
export const Theme = z.enum(['auto', 'light', 'dark']);
export type Theme = z.infer<typeof Theme>;

// --- Companions (girlfriends) ----------------------------------------------

/** `girlfriends.status` — builder lifecycle: draft → building → ready. */
export const GfStatus = z.enum(['draft', 'building', 'ready', 'disabled']);
export type GfStatus = z.infer<typeof GfStatus>;

/**
 * `girlfriends.build_source` — how the companion was created:
 * `describe` attributes, `upload` a concept image, or `prebuilt` (cloned from a
 * catalog template — the free-start onboarding path, docs/idea/companions.md).
 */
export const BuildSource = z.enum(['describe', 'upload', 'prebuilt']);
export type BuildSource = z.infer<typeof BuildSource>;

/** `girlfriends.nsfw_level` — adult-only; two content intensities. */
export const NsfwLevel = z.enum(['suggestive', 'explicit']);
export type NsfwLevel = z.infer<typeof NsfwLevel>;

// --- Chat -------------------------------------------------------------------

/**
 * `messages.role` — AI SDK message roles. data-model.md keys the column as
 * `role` without enumerating values; these are the AI SDK v6 roles the agent
 * loop emits/consumes (see docs/idea/agent.md).
 */
export const MessageRole = z.enum(['system', 'user', 'assistant', 'tool']);
export type MessageRole = z.infer<typeof MessageRole>;

/** `messages.origin` — `proactive` = she texted first (proactive-messaging.md). */
export const MessageOrigin = z.enum(['user', 'agent', 'proactive']);
export type MessageOrigin = z.infer<typeof MessageOrigin>;

// --- Memory -----------------------------------------------------------------

/** `memories.kind` — per-companion durable recall categories. */
export const MemoryKind = z.enum(['fact', 'preference', 'summary']);
export type MemoryKind = z.infer<typeof MemoryKind>;

// --- Media (images / videos) -----------------------------------------------

/** `media_jobs.kind` / `media_assets.kind` — the two generated media types. */
export const MediaKind = z.enum(['image', 'video']);
export type MediaKind = z.infer<typeof MediaKind>;

/**
 * `media_jobs.status` — `rejected` = a red-lined output (auto-blocked, never billed);
 * `held` = an UNCERTAIN (`review`) output the worker never auto-serves — the bytes are
 * kept for a moderator and the job waits in the admin review queue (never billed until
 * a moderator acts). Distinct from `rejected`: a hold is a pending human decision, not a
 * terminal "no". `text` column + Zod (no pg enum), so a new state needs no migration.
 */
export const MediaJobStatus = z.enum(['queued', 'running', 'done', 'failed', 'rejected', 'held']);
export type MediaJobStatus = z.infer<typeof MediaJobStatus>;

/** `media_assets.visibility` — private (presigned GET) vs public (e2 bucket). */
export const Visibility = z.enum(['private', 'public']);
export type Visibility = z.infer<typeof Visibility>;

/**
 * `accounts.disabled_reason` — WHY a tenant is disabled, because one nullable
 * timestamp had to mean two unrelated things.
 *
 * `ban` is a moderator/red-line action whose evidence must be RETAINED;
 * `deletion_requested` is the user asking to be erased and starts the grace clock
 * toward an irreversible purge. Reading `disabled_at` alone cannot tell them
 * apart, which is how a purge could erase the very material a ban preserved.
 *
 * Null on a row disabled before this column existed: an UNKNOWN reason, which
 * every destructive path must treat as "not purgeable" rather than guess.
 */
export const DisabledReason = z.enum(['ban', 'deletion_requested']);
export type DisabledReason = z.infer<typeof DisabledReason>;

// --- Billing & credits ------------------------------------------------------

/** `subscriptions.status` — CCBill subscription lifecycle. */
export const SubscriptionStatus = z.enum(['active', 'past_due', 'canceled']);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatus>;

/**
 * `credit_ledger.reason` — every append-only ledger entry's cause. `achievement`
 * is included per the engagement section (credit_ledger reason='achievement');
 * the rest are the data-model.md list.
 */
export const LedgerReason = z.enum([
  'subscription_grant',
  'topup',
  'chat',
  'image',
  'video',
  'referral_signup',
  'referral_revshare',
  'achievement',
  'refund',
  'adjustment',
]);
export type LedgerReason = z.infer<typeof LedgerReason>;

/** `usage_events.kind` — metered upstream-cost event types. */
export const UsageKind = z.enum(['chat', 'image', 'video', 'embedding', 'web_search']);
export type UsageKind = z.infer<typeof UsageKind>;

// --- Referrals --------------------------------------------------------------

/** `referrals.status` — `qualified` after age-verify + anti-fraud. */
export const ReferralStatus = z.enum(['pending', 'qualified', 'revoked']);
export type ReferralStatus = z.infer<typeof ReferralStatus>;

// --- Proactive messaging ----------------------------------------------------

/** `proactive_schedules.cadence` — how often she texts first (`off` = never). */
export const Cadence = z.enum(['off', 'low', 'medium', 'high']);
export type Cadence = z.infer<typeof Cadence>;

// --- Engagement / achievements ---------------------------------------------

/** `achievements.category` — unlockable achievement grouping. */
export const AchievementCategory = z.enum(['onboarding', 'streak', 'creation', 'social', 'spend']);
export type AchievementCategory = z.infer<typeof AchievementCategory>;

// --- Staff / admin ----------------------------------------------------------

/** `staff_users.role` — admin console access level (separate from consumers). */
export const StaffRole = z.enum(['admin', 'moderator', 'support']);
export type StaffRole = z.infer<typeof StaffRole>;

/**
 * A moderator's decision on a held (`review`) `moderation_events` row.
 * `approve` clears it (`verdict → allow`); `reject`/`ban` block it
 * (`verdict → block`); `ban` additionally hard-disables the offender's account.
 */
export const ModerationAction = z.enum(['approve', 'reject', 'ban']);
export type ModerationAction = z.infer<typeof ModerationAction>;

/**
 * `staff_audit_log.action` — the staff operation an audit row records. Every
 * privileged mutation (a moderation decision, a data-rights trigger) writes one
 * with the actor + before/after snapshot, so the console is fully accountable.
 *
 * `moderation.evidence.view` is the one NON-mutating member: revealing the held
 * text/media behind a queue item is itself audited, because a red-line decision
 * (approve/ban on CSAM or NCII) may only be taken by an operator who actually
 * looked. The reveal audit row MUST be written before the content is returned —
 * an un-audited reveal is worse than no reveal (docs/idea/compliance.md).
 */
export const StaffAuditAction = z.enum([
  'moderation.approve',
  'moderation.reject',
  'moderation.ban',
  // The audited REVEAL of an item's evidence — attributable to one operator, one
  // item, one instant. There must be exactly ONE spelling of this action: the
  // sighted-review check reads the audit log for the value the reveal path
  // writes, so a second synonym would make the gate silently miss a real reveal.
  'moderation.evidence.view',
  // The decision COMMITTED but acting on the held artifact behind it did not
  // (an e2 outage, a vanished object). Recorded as its own entry, after the
  // fact, because the decision's own entry is written inside the transaction
  // that flips the verdict — before the artifact can possibly have been touched.
  // Without it a `reject` whose purge failed is indistinguishable, in the trail,
  // from one whose bytes are actually gone.
  'moderation.artifact.failed',
  'user.export',
  'user.delete',
]);
export type StaffAuditAction = z.infer<typeof StaffAuditAction>;

// --- Moderation -------------------------------------------------------------

/**
 * `moderation_events.subject` — what was classified. Pre-generation subjects
 * (`prompt`, `upload`, `persona`) come from the input gate; post-generation
 * subjects (`text`, `image`, `video`) from the output gate. `text` is the
 * ASSISTANT turn the model produced, screened before it is persisted — compliance.md
 * requires output classifiers "on the generated image/video/**text**", and without
 * this subject the chat half of the output gate had nowhere to log.
 */
export const ModerationSubject = z.enum(['prompt', 'text', 'image', 'video', 'upload', 'persona']);
export type ModerationSubject = z.infer<typeof ModerationSubject>;

/** `moderation_events.verdict` — the two red lines always `block` + log. */
export const ModerationVerdict = z.enum(['allow', 'block', 'review']);
export type ModerationVerdict = z.infer<typeof ModerationVerdict>;

/**
 * The two ABSOLUTE compliance red lines (docs/idea/compliance.md § "Two absolute
 * red lines"), as classifier category keys. The ONE cross-layer source both the
 * backend enforcement gates (`moderation/red-lines.ts`) and the admin queue's
 * red-line flag (`features/moderation/moderation-model.ts`) key off, so a rename
 * or a newly added red line can never leave one layer unflagged. Values are the
 * EXACT strings written to `moderation_events.categories`.
 */
export const RedLineCategory = z.enum(['minor', 'real_person']);
export type RedLineCategory = z.infer<typeof RedLineCategory>;

/** Red line 1 — CSAM / apparent-minor category key. */
export const RED_LINE_MINOR: RedLineCategory = 'minor';
/** Red line 2 — NCII / real-person-deepfake category key. */
export const RED_LINE_REAL_PERSON: RedLineCategory = 'real_person';

/** The two red-line category keys, in stable order (drives `redLineHits` + the admin flag). */
export const RED_LINE_CATEGORIES: readonly RedLineCategory[] = RedLineCategory.options;

/** True when a classifier category key is one of the two absolute red lines. */
export function isRedLineCategory(key: string): key is RedLineCategory {
  return (RED_LINE_CATEGORIES as readonly string[]).includes(key);
}
